/**
 * Check a request body against the API's own validators (`src/api/generated`) before sending it, and place a 422's
 * messages beside the fields they name. The server is the authority; this only spares a round trip.
 */

import type * as zod from 'zod';

import { ApiError } from './client';

/** Messages by field path (`name`, `metrics.0.value`); empty when the body is valid. */
export type FieldErrors = Record<string, string>;

/**
 * The server strips whitespace from text before checking it and the generated validators don't, so every string is
 * trimmed first: `"  "` is then empty here too, and padding does not count toward a length limit. Passwords are the
 * exception: the server takes them as typed.
 */
export function trimmed(value: unknown): unknown {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.map(trimmed);
  if (value !== null && typeof value === 'object' && !(value instanceof Blob)) {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, k.endsWith('password') ? v : trimmed(v)]));
  }
  return value;
}

function defaultMessage(issue: zod.core.$ZodIssue): string {
  if (issue.code === 'too_big' && issue.origin === 'string') return `At most ${issue.maximum} characters.`;
  if (issue.code === 'too_big' && issue.origin === 'array') return `At most ${issue.maximum}.`;
  if (issue.code === 'too_small' && issue.origin === 'string') return 'Required.';
  if (issue.code === 'too_small' && issue.origin === 'array') return `At least ${issue.minimum}.`;
  return 'Invalid value.';
}

/**
 * The first problem per field path of `body` (trimmed) under `schema`. `messages` words a path's problem the way the
 * form already does; any other path gets a plain default.
 */
export function fieldErrors(schema: zod.ZodType, body: unknown, messages: Readonly<Record<string, string>> = {}): FieldErrors {
  const result = schema.safeParse(trimmed(body));
  const errors: FieldErrors = {};
  if (result.success) return errors;
  for (const issue of result.error.issues) {
    const path = issue.path.map(String).join('.');
    if (!(path in errors)) errors[path] = messages[path] ?? defaultMessage(issue);
  }
  return errors;
}

/** `value` when `schema` (an enum from the contract) accepts it, else `fallback`: narrows a wire string. */
export function oneOf<T, F = T>(schema: zod.ZodType<T>, value: unknown, fallback: F): T | F {
  const result = schema.safeParse(value);
  return result.success ? result.data : fallback;
}

/** A 422's messages by field path, or none for any other error. */
export function serverFieldErrors(error: unknown): FieldErrors {
  return error instanceof ApiError && error.status === 422 ? { ...error.fields } : {};
}

/**
 * What a form's banner still says once the 422 messages for `shown` paths sit beside their fields: null when every
 * message is placed, the error's own message otherwise.
 */
export function bannerError(error: unknown, shown: readonly string[]): string | null {
  if (!error) return null;
  const fields = serverFieldErrors(error);
  const paths = Object.keys(fields);
  if (paths.length > 0 && paths.every((p) => shown.includes(p))) return null;
  return error instanceof Error ? error.message : String(error);
}
